import { yupResolver } from '@hookform/resolvers/yup';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Grid, Card, IconButton } from '@mui/material';
import { useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FieldValues, useForm, FormProvider } from 'react-hook-form';
import { MdKeyboardArrowRight } from 'react-icons/md';
import {
  IJob,
  jobDescriptionGenerator,
  getSkillsSuggestions,
  JobType,
  amplitude,
} from 'wisestep-api';
import {
  ControlledTextInput,
  Label,
  WsAutoComplete,
  WsButton,
  WsButtonGroup,
  WsColors,
  WsDialog,
  WsDialogSize,
  WsInputNumber,
  WsLocationSearch,
  WsSelect,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';
import { object, string, array } from 'yup';

import { useWorkspaceContext } from '../../../../../src/context';
import ErrorLogger from '../../../../providers/ErrorLogger';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { transformISkillsToStringArray } from '../../helper';

import JDGeneratorDetailsDialog from './JDGeneratorDetailsDialog';
import JDGeneratorLoadingDialog from './JDGeneratoreLoadingDialog';
import {
  aboutUsPlaceHolder,
  industryCategories,
  jdGeneratedFailed,
  jdGeneratedSuccess,
  jdGenerator,
  jobTypeList,
  StyledTextareaAutosize,
  jdGeneratedError,
} from './jdGeneratorHelper';

export interface JDGeneratorDialogProps {
  open: boolean;
  onClose: (value: string) => void;
  onJDGeneration: (job: IJob) => void;
}

const validationSchema = object().shape({
  jobtitle: string().required('Job Title is required'),
  skills: array().of(string()).required('Please enter candidate skills'),
  location: array().min(1, 'Please select at-least one location'),
});

const JDGeneratorDialog = (props: JDGeneratorDialogProps) => {
  const formOptions = {
    resolver: yupResolver(validationSchema),
  };
  const methods = useForm(formOptions);
  const workspace = useWorkspaceContext();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const { open, onClose, onJDGeneration } = props;
  const [loader, setLoader] = useState<boolean>(false);
  const [jobEntity, setJobEntity] = useState<IJob>();
  const { handleSubmit, setValue, getValues } = methods;

  const [skillList, setSkillList] = useState<string[]>([]);

  const onSkillSelection = (skillTags: string[]) => setValue('skills', skillTags ?? []);

  const onSkillInputKey = async (skillInput: string) => {
    const result = await getSkillsSuggestions(skillInput);
    if (result.data) {
      setSkillList(transformISkillsToStringArray(result.data));
    }
  };
  const [showJdGeneratedUi, setShowJdGeneratedUi] = useState(true);
  const onJDParseSubmit = async (data: FieldValues) => {
    setLoader(true);
    try {
      amplitude('Generate_JD_Submit"', {
        clickedOn: 'Generate_JD_Submit_Button',
        pageName: 'New Job',
        pageUrl: window.location.href,
        workspaceName: workspace.name,
        planType: workspace.planType,
        interfaceType: 'Web',
      });

      jdGenerator.jobtitle = data.jobtitle?.trim();
      jdGenerator.location = data.location;
      jdGenerator.skills = data.skills;
      jdGenerator.jobType = data.jobType;
      jdGenerator.yoe = data.yoe;
      jdGenerator.about_company = data.about_company;
      jdGenerator.jobType = getValues('jobType');
      jdGenerator.industry = data.industry;

      const response = await jobDescriptionGenerator(jdGenerator);
      const generatedContent = response.data.results.generated_content;
      if (response.data.results.errors.length > 0) {
        setNotification(jdGeneratedError());
      }
      const tempJob: IJob = {
        description: generatedContent,
        title: jdGenerator.jobtitle,
        locations: jdGenerator.location ?? [],
        skills: jdGenerator.skills,
        jobType: jdGenerator.jobType,
        maximumExperienceInYears: jdGenerator.yoe,
        companyName: jdGenerator.about_company,
        industry: jdGenerator.industry,
      };
      setJobEntity(tempJob);
      setNotification(jdGeneratedSuccess());
      setLoader(false);
      setShowJdGeneratedUi(false);
    } catch (error) {
      setLoader(false);
      setNotification(jdGeneratedFailed());
    }
  };
  const handleBackToForm = () => setShowJdGeneratedUi(true);

  return (
    <>
      <WsDialog aria-labelledby="Jd upload" size={WsDialogSize.LARGE} openDialog={open}>
        {loader ? (
          <Grid container justifyContent="center" p={2}>
            <Card sx={{ minHeight: 300, alignItems: 'center', width: '100%' }}>
              <Grid item container xs={12} justifyContent="flex-end" p={1.5}>
                <CloseIcon
                  sx={{
                    cursor: 'pointer',
                  }}
                  fontSize="medium"
                  onClick={() => onClose('choice')}
                />
              </Grid>
              <JDGeneratorLoadingDialog />
            </Card>
          </Grid>
        ) : (
          <FormProvider {...methods}>
            <Box bgcolor={WsColors.TertiaryShades50}>
              <Grid container p={1}>
                {!showJdGeneratedUi && (
                  <Grid item container xs={0.5} justifyContent="flex-start">
                    <IconButton onClick={handleBackToForm}>
                      <ArrowBackIcon />
                    </IconButton>
                  </Grid>
                )}
                <Grid item container xs={10.5} justifyContent="flex-start" pt={0.5}>
                  <WsTypography variant={WsTypoVariants.SUB_TITLE}>
                    Generate Job Description
                  </WsTypography>
                </Grid>
                <Grid item container xs={1} justifyContent="flex-end">
                  <CloseIcon
                    sx={{
                      cursor: 'pointer',
                    }}
                    fontSize="medium"
                    onClick={() => onClose('choice')}
                  />
                </Grid>
              </Grid>
            </Box>
            {showJdGeneratedUi ? (
              <>
                <Grid container xs={12} p={2} justifyContent="center" spacing={2}>
                  <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                    <Grid item xs={5.5} pr={1}>
                      <ControlledTextInput
                        name="jobtitle"
                        id="outlined-basic"
                        labelText="Job Title"
                        placeholder="Eg, Java Developer"
                        showMandatorySymbol={true}
                        autoComplete="title"
                        variant="outlined"
                        size="small"
                        type="text"
                        fullWidth
                        required
                      />
                    </Grid>
                  </ErrorBoundary>
                  <Grid item xs={5.5} pl={1}>
                    <WsLocationSearch
                      size="small"
                      placeHolder="Job Location"
                      name="location"
                      variant="outlined"
                      showMandatorySymbol={true}
                      required
                    />
                  </Grid>
                  <Grid item xs={11}>
                    <WsAutoComplete
                      onValueEnter={onSkillInputKey}
                      onTagsChange={onSkillSelection}
                      placeholder="Type to Select Skills"
                      initialValue={[]}
                      optionsInStringArray={skillList}
                      multiple={true}
                      freeSolo={true}
                      name="skills"
                      label="Skills"
                      variant="outlined"
                      size="small"
                      showMandatorySymbol={true}
                      required
                    />
                  </Grid>
                  <Grid item xs={11}>
                    <WsButtonGroup
                      inputTitle="Job Type"
                      buttonsList={jobTypeList}
                      selectedIndexValue={(index: number, value: string) =>
                        methods.setValue('jobType', value as unknown as JobType)
                      }
                      defaultValue={2}
                    />
                  </Grid>
                  <Grid item xs={5.5} pr={1}>
                    <WsSelect
                      label="Industry"
                      name="industry"
                      selectArray={industryCategories}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={5.5} pr={1}>
                    <WsInputNumber name="yoe" label="Years Of Experience" variant="outlined" />
                  </Grid>
                  <Grid item xs={11}>
                    <Label>About the company</Label>
                    <StyledTextareaAutosize
                      name="about_company"
                      minRows={10}
                      placeholder={aboutUsPlaceHolder}
                      defaultValue={''}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12} p={2} container justifyContent="flex-end" position="static">
                  <WsButton
                    variant="contained"
                    endIcon={<MdKeyboardArrowRight />}
                    type="submit"
                    onClick={handleSubmit(onJDParseSubmit)}
                  >
                    Generate
                  </WsButton>
                </Grid>
              </>
            ) : (
              jobEntity && (
                <JDGeneratorDetailsDialog
                  jobDescriptionData={jobEntity}
                  onJDGeneration={onJDGeneration}
                  onRegenerate={handleSubmit(onJDParseSubmit)}
                />
              )
            )}
          </FormProvider>
        )}
      </WsDialog>
    </>
  );
};

export default JDGeneratorDialog;
